/**
 * Demo panel figures for the THREE non-Banking industry bands of Layer 1.
 *
 * DEMO ONLY, PLA-8828 2026-09-21 — invented, like everything under the `DEMO_FALLBACK_ENABLED`
 * switch, and deleted with it.
 *
 * WHY THIS FILE EXISTS. `sections/IndustrySection.tsx` draws a bespoke panel body for each of the
 * seven non-Banking industry cards — rings, segmented bars, band legends and ranked evidence lists —
 * and feeds all of them from three module-scope objects (`telco`, `insurance`, `fintech`) whose
 * every field ships EMPTY, because no Layer 1 response carries a panel breakdown. Banking got its
 * demo equivalents (`DEMO_BANKING_SPEND` and the three beside it in `data/demoFallback.ts`); these
 * three never did.
 *
 * That was harmless while the tiles were blank, because each panel is gated on
 * `hasLiveReadings(metrics, INDUSTRY_PANEL_METRICS.…)` and an unread tile kept its panel unmounted.
 * It stopped being harmless the moment the demo layer filled those tiles: the gates now OPEN on
 * every one of the seven cards, so the panels mount and draw empty rings, empty legends and `NaN`
 * quotients the moment the tenant industry is switched to fintech, insurance or telco.
 *
 * EVERY FIGURE HERE IS CONSISTENT WITH THE TILE ABOVE IT. The tile values live in
 * `data/demoFallback.ts` (`DEMO_FALLBACK_TABLE.customer_insights`) and a panel that restates one
 * must not restate it differently, so each quotient below divides out to the tile it sits under:
 * telco `39,008 / 2,120` is the 18.4 ARPU tile, insurance `1,880 / 2,120` is the 88.7% active-policy
 * tile, fintech `1,374 / 2,120` is the 64.8% wallet-active tile. Every count is a slice of the same
 * 2,500 registered / 2,120 eligible population the rest of the demo is built on.
 *
 * WHAT IS NOT HERE, and is not negotiable: no `reason_code`, no `calculation_status`, no
 * `Why this result?` narrative. Card confidence pills are `DEMO_CARD_CONFIDENCE`'s in
 * `data/demoFallback.ts` and are not duplicated here.
 *
 * THE SHAPES ARE STRUCTURAL, not imported. `IndustrySection` declares the three object types inline
 * and exports neither, so each is restated below as its own interface and TypeScript checks the two
 * against each other structurally. `LabelledCount` is `sections/distributions.ts`'s, restated for
 * the same reason and to keep a `data/` module from importing a `sections/` one.
 *
 * READING THE UNITS, because the three collections below are NOT all in the same one:
 *   - `…Bands`     — COUNTS. `toDistributionSegments` / `toLegendRows` divide each by the set's own
 *                    total, so a band set states its population by summing to it.
 *   - `…Evidence`, `…Drivers`, `…Signals`, `featureAdoption`, `diversityUseDistribution` —
 *                    PERCENTS on the 0-100 scale, via the section's own `toPercentItems`.
 *   - `…Trend`     — the series the panel's sparkline plots. `arpuTrend` is stated in MILLIONS (the
 *                    section multiplies by 1e6 before formatting); `walletActiveRateTrend` is a
 *                    percent on the 0-100 scale.
 *   - `components` — a composite score's parts, which SUM TO THAT SCORE and may be negative.
 */

/** One labelled figure — `sections/distributions.ts`'s `LabelledCount`, restated structurally. */
interface DemoLabelledCount {
  label: string;
  value: number;
}

/** A composite score and the parts it is summed from — `IndustrySection`'s `IIndustryScore`. */
interface DemoIndustryScore {
  score?: number;
  components: DemoLabelledCount[];
}

/** The shape of `IndustrySection`'s `telco` object, restated structurally. */
export interface DemoTelcoPanels {
  serviceRiskBands: DemoLabelledCount[];
  serviceRiskEvidence: DemoLabelledCount[];
  churnRiskBands: DemoLabelledCount[];
  churnRiskDrivers: DemoLabelledCount[];
  dataUtilisationBands: DemoLabelledCount[];
  upgradeReadinessBands: DemoLabelledCount[];
  upgradeReadinessEvidence: DemoLabelledCount[];
  arpuTrend: number[];
  arpu: { revenue?: number; subscribers?: number };
  allowance: {
    dataUsedPb?: number;
    dataIncludedPb?: number;
    voiceUsedMinutes?: number;
    voiceIncludedMinutes?: number;
  };
  rechargeRegularity: DemoIndustryScore & { medianPerMonth?: number; medianAmount?: number };
  serviceRiskEligible?: number;
  networkEventFreshnessMinutes?: number;
  churnActiveSubscribers?: number;
}

/** The shape of `IndustrySection`'s `insurance` object, restated structurally. */
export interface DemoInsurancePanels {
  policyStatus: DemoLabelledCount[];
  renewalRiskBands: DemoLabelledCount[];
  renewalRiskDrivers: DemoLabelledCount[];
  coverageGapSignals: DemoLabelledCount[];
  riderReadinessBands: DemoLabelledCount[];
  policyActive: { active?: number; eligible?: number };
  premiumHealth: DemoIndustryScore;
  claims: {
    count?: number;
    activePolicies?: number;
    claimAmount?: number;
    premiumAmount?: number;
  };
  renewalDuePolicies?: number;
  renewalHighRisk?: number;
}

/** The shape of `IndustrySection`'s `fintech` object, restated structurally. */
export interface DemoFintechPanels {
  anomalySeverity: DemoLabelledCount[];
  anomalySignals: { label: string; test: string; value: number }[];
  anomalySource: { ruleTriggered?: number; statistical?: number; latencyMinutes?: number };
  diversityIndex: DemoIndustryScore;
  diversityUseDistribution: DemoLabelledCount[];
  featureAdoption: DemoLabelledCount[];
  topupScore: DemoIndustryScore & { medianPerMonth?: number; medianAmount?: number };
  walletActive: { active?: number; eligible?: number };
  walletActiveRateTrend: number[];
}

/* The eligible population every count below is a slice of, the same figure `data/demoFallback.ts`
   takes its own counts over. Stated here rather than imported so this file has no dependency on the
   one that will import it. */
const ELIGIBLE = 2120;

/* ==============================================================================================
   TELCO — Churn & Service Experience, Plan Usage & Revenue, Recharge & Upgrade Readiness.
   ============================================================================================== */

export const DEMO_TELCO_PANELS: DemoTelcoPanels = {
  serviceRiskEligible: ELIGIBLE,
  /* Sums to the 2,120 the panel heading names. The two adverse bands are 275 + 111 = 386, which IS
     the `subscribers_with_service_risk` tile, and 386/2,120 is its 18.2% rate — so the bar, the
     legend and both tiles partition one population. */
  serviceRiskBands: [
    { label: 'Low', value: 1268 },
    { label: 'Moderate', value: 466 },
    { label: 'High', value: 275 },
    { label: 'Critical', value: 111 }
  ],
  // Shares of the card's own evidence, so they sum to 100.
  serviceRiskEvidence: [
    { label: 'Dropped call events', value: 34.2 },
    { label: 'Data session failures', value: 27.5 },
    { label: 'Repeat support contacts', value: 22.8 },
    { label: 'Billing complaints', value: 15.5 }
  ],
  networkEventFreshnessMinutes: 12,
  churnActiveSubscribers: ELIGIBLE,
  /* Same population, split on a different question: 168 + 74 = 242 is the
     `high_risk_active_subscribers` tile and 242/2,120 its 11.4% rate. */
  churnRiskBands: [
    { label: 'Low', value: 1402 },
    { label: 'Moderate', value: 476 },
    { label: 'High', value: 168 },
    { label: 'Critical', value: 74 }
  ],
  churnRiskDrivers: [
    { label: 'Plan value mismatch', value: 31.6 },
    { label: 'Service experience issues', value: 26.4 },
    { label: 'Competitor offer exposure', value: 23.1 },
    { label: 'Recharge lapse', value: 18.9 }
  ],
  /* Four bands because `CATEGORY_COLORS` is four long. Weighted at each band's midpoint the set
     averages 72.6%, which is the `data_utilisation_rate` tile — the distribution and the tile are
     the same measurement drawn twice. */
  dataUtilisationBands: [
    { label: 'Under 25%', value: 96 },
    { label: '25-50%', value: 205 },
    { label: '50-75%', value: 566 },
    { label: 'Over 75%', value: 1253 }
  ],
  // 39,008 / 2,120 = 18.40, the `current_arpu` tile, and 39,008 the `total_billable_revenue` one.
  arpu: { revenue: 39008, subscribers: ELIGIBLE },
  /* Stated in MILLIONS: the panel formats each point as `point * 1e6`. The last point is the
     39,008 above, so the trend lands on the figure the readings beside it print. */
  arpuTrend: [0.0352, 0.0359, 0.0367, 0.0374, 0.0383, 0.039008],
  /* 36.3/50 = 72.6% and 1,023,960/2,120,000 = 48.3%, the two utilisation tiles. The voice allowance
     is 1,000 minutes per eligible subscriber. The data allowance is NOT at this population's scale —
     2,120 subscribers do not consume 36 PB — because the panel's unit is the designer's, sized for
     the 1.15M-subscriber board it was drawn on; the RATIO is what the tile restates and that is
     exact. Recorded rather than silently rescaled: changing the unit is a design decision. */
  allowance: {
    dataUsedPb: 36.3,
    dataIncludedPb: 50,
    voiceUsedMinutes: 1023960,
    voiceIncludedMinutes: 2120000
  },
  // The four components sum to 71, the `recharge_regularity_score` tile.
  rechargeRegularity: {
    score: 71,
    components: [
      { label: 'On-time recharge cadence', value: 34 },
      { label: 'Top-up amount stability', value: 26 },
      { label: 'Channel consistency', value: 20 },
      { label: 'Missed expected recharge', value: -9 }
    ],
    medianPerMonth: 3,
    medianAmount: 12.5
  },
  /* 372 + 126 = 498 is the `upgrade_growth_planner_eligible` tile and 498/2,120 its 23.5% readiness
     rate. */
  upgradeReadinessBands: [
    { label: 'Not ready', value: 1004 },
    { label: 'Emerging', value: 618 },
    { label: 'Ready', value: 372 },
    { label: 'Highly ready', value: 126 }
  ],
  upgradeReadinessEvidence: [
    { label: 'Allowance exhaustion', value: 38.4 },
    { label: 'Device age over 24 months', value: 27.1 },
    { label: 'Repeat add-on purchases', value: 20.3 },
    { label: 'Roaming usage', value: 14.2 }
  ]
};

/** The empty set the section ships today, for the `DEMO_FALLBACK_ENABLED = false` branch. */
export const NO_TELCO_PANELS: DemoTelcoPanels = {
  serviceRiskBands: [],
  serviceRiskEvidence: [],
  churnRiskBands: [],
  churnRiskDrivers: [],
  dataUtilisationBands: [],
  upgradeReadinessBands: [],
  upgradeReadinessEvidence: [],
  arpuTrend: [],
  arpu: {},
  allowance: {},
  rechargeRegularity: { components: [] }
};

/* ==============================================================================================
   INSURANCE — Policy Health & Claims, Renewal & Growth Opportunities.
   ============================================================================================== */

export const DEMO_INSURANCE_PANELS: DemoInsurancePanels = {
  // 1,880 / 2,120 = 88.7%, the ring's centre and the `policy_active_rate` tile.
  policyActive: { active: 1880, eligible: ELIGIBLE },
  /* Three bands, not four: `RENEWAL_COLORS` is three long and a fourth would draw uncoloured. The
     set sums to 2,120 and its Active band is the 1,880 the ring counts. */
  policyStatus: [
    { label: 'Active', value: 1880 },
    { label: 'Lapsed', value: 176 },
    { label: 'Grace period', value: 64 }
  ],
  // The five components sum to 81, the `premium_payment_health` tile.
  premiumHealth: {
    score: 81,
    components: [
      { label: 'On-time premium payments', value: 42 },
      { label: 'Auto-debit coverage', value: 23 },
      { label: 'Grace-period recoveries', value: 11 },
      { label: 'Payment method stability', value: 9 },
      { label: 'Missed premium instalments', value: -4 }
    ]
  },
  /* 639 / 1,880 = 0.34, the `claims_frequency` tile, taken over the SAME active-policy count the
     ring above reports. $1.86M / $4.24M = 44.0% is the claim-to-premium ratio beside it. */
  claims: {
    count: 639,
    activePolicies: 1880,
    claimAmount: 1864000,
    premiumAmount: 4236000
  },
  /* The renewal band is taken over the policies DUE for renewal, not the whole book: 148 + 56 = 204
     high-risk of 1,214 due is 16.8%, the `renewal_risk_rate` tile. */
  renewalDuePolicies: 1214,
  renewalHighRisk: 204,
  renewalRiskBands: [
    { label: 'Low', value: 726 },
    { label: 'Moderate', value: 284 },
    { label: 'High', value: 148 },
    { label: 'Critical', value: 56 }
  ],
  renewalRiskDrivers: [
    { label: 'Premium increase at renewal', value: 33.8 },
    { label: 'Lapsed auto-debit', value: 26.2 },
    { label: 'No claim in term', value: 22.4 },
    { label: 'Competitor quote activity', value: 17.6 }
  ],
  coverageGapSignals: [
    { label: 'Sum insured below benchmark', value: 31.5 },
    { label: 'No health rider', value: 27.8 },
    { label: 'Dependants uncovered', value: 22.6 },
    { label: 'No critical-illness cover', value: 18.1 }
  ],
  /* 332 + 128 = 460 is the `growth_planner_eligible` tile and 460/2,120 its 21.7%
     `rider_cross_sell_readiness` rate. */
  riderReadinessBands: [
    { label: 'Not ready', value: 1035 },
    { label: 'Emerging', value: 625 },
    { label: 'Ready', value: 332 },
    { label: 'Highly ready', value: 128 }
  ]
};

/** The empty set the section ships today, for the `DEMO_FALLBACK_ENABLED = false` branch. */
export const NO_INSURANCE_PANELS: DemoInsurancePanels = {
  policyStatus: [],
  renewalRiskBands: [],
  renewalRiskDrivers: [],
  coverageGapSignals: [],
  riderReadinessBands: [],
  policyActive: {},
  premiumHealth: { components: [] },
  claims: {}
};

/* ==============================================================================================
   FINTECH / WALLET — Wallet Activity & Funding, Payment Usage & Risk.
   ============================================================================================== */

export const DEMO_FINTECH_PANELS: DemoFintechPanels = {
  // 1,374 / 2,120 = 64.8%, the `wallet_active_user_rate` tile, over the `active_eligible_users` one.
  walletActive: { active: 1374, eligible: ELIGIBLE },
  // Percent on the 0-100 scale, landing on the 64.8 the readings beside it print.
  walletActiveRateTrend: [58.4, 60.1, 61.7, 62.9, 63.8, 64.8],
  // The four components sum to 58, the `topup_frequency_score` tile.
  topupScore: {
    score: 58,
    components: [
      { label: 'Top-up cadence', value: 26 },
      { label: 'Amount consistency', value: 19 },
      { label: 'Funding-source stability', value: 15 },
      { label: 'Failed top-up attempts', value: -2 }
    ],
    medianPerMonth: 4,
    medianAmount: 28.5
  },
  /* Independent adoption rates rather than shares of one total — `toPercentItems` gives each bar its
     own percent — so these do not sum to 100. The leading 42.9 is the `highest_feature_adoption`
     tile, which reports the single highest-adopting feature. */
  featureAdoption: [
    { label: 'Bill payments', value: 42.9 },
    { label: 'P2P transfers', value: 35.4 },
    { label: 'QR merchant pay', value: 28.7 },
    { label: 'Linked-card spend', value: 19.6 },
    { label: 'Savings pocket', value: 11.2 }
  ],
  /* 0.21 + 0.19 + 0.16 + 0.10 + 0.06 = 0.72, the `payment_diversity_index` tile.
     The index is NORMALISED 0-1, not an entropy in bits: `DIVERSITY_SHARE` divides the score by a
     scale of 1 (`IndustrySection.tsx`, `shareOf(fintech.diversityIndex.score, 1)`) and `indexText`'s
     own comment names the design's value — "would print the ring's 0.72 as 0.720 against the tile's
     0.72". `shareOf` CLAMPS, so an out-of-range score does not throw or print `NaN`; it sweeps a
     full arc with the out-of-range figure in the centre, which looks deliberate rather than broken.
     Each part is stated to the two decimals `indexText` prints, so the panel's rows visibly add up
     to the ring's centre instead of missing it by a rounding step. */
  diversityIndex: {
    score: 0.72,
    components: [
      { label: 'QR merchant pay', value: 0.21 },
      { label: 'Bill payments', value: 0.19 },
      { label: 'P2P transfers', value: 0.16 },
      { label: 'Card spend', value: 0.1 },
      { label: 'ATM withdrawal', value: 0.06 }
    ]
  },
  diversityUseDistribution: [
    { label: 'QR merchant pay', value: 28.4 },
    { label: 'Bill payments', value: 24.1 },
    { label: 'P2P transfers', value: 21.7 },
    { label: 'Card spend', value: 15.3 },
    { label: 'ATM withdrawal', value: 10.5 }
  ],
  /* Sums to 78, which is both the `anomalous_wallet_users` tile and the population the panel heading
     names — the section takes that heading's figure from this set's own total. 78/2,120 is the 3.7%
     `wallet_velocity_anomaly_rate` beside it. */
  anomalySeverity: [
    { label: 'Low', value: 34 },
    { label: 'Moderate', value: 25 },
    { label: 'High', value: 13 },
    { label: 'Critical', value: 6 }
  ],
  // Shares of the flagged population, so they sum to 100.
  anomalySignals: [
    { label: 'Velocity spike', test: 'z-score > 3', value: 38.5 },
    { label: 'Structuring pattern', test: 'rule R-114', value: 26.9 },
    { label: 'Dormant reactivation', test: 'z-score > 2.5', value: 20.5 },
    { label: 'Device change burst', test: 'rule R-207', value: 14.1 }
  ],
  // The two detection routes partition the same flagged population: 62.8 + 37.2 = 100.
  anomalySource: { ruleTriggered: 62.8, statistical: 37.2, latencyMinutes: 4 }
};

/** The empty set the section ships today, for the `DEMO_FALLBACK_ENABLED = false` branch. */
export const NO_FINTECH_PANELS: DemoFintechPanels = {
  anomalySeverity: [],
  anomalySignals: [],
  anomalySource: {},
  diversityIndex: { components: [] },
  diversityUseDistribution: [],
  featureAdoption: [],
  topupScore: { components: [] },
  walletActive: {},
  walletActiveRateTrend: []
};

/* ==============================================================================================
   CUSTOMER 360 — the per-product Product Readiness layouts.

   `customer360/productReadiness/registry.ts` resolves a bespoke component per product, and each one
   draws a frame full of blocks the readiness schema has no column for: policy context tiles, a
   premium-payment donut, a recommendation comparison, a capacity ramp. All of them ship EMPTY.
   Unlike the three industry objects above this is not a regression — these blocks were always blank,
   because nothing opens or closes them — but they are the body of the card an INSURANCE tenant lands
   on by default (`policy_renewal` is that industry's `recommendedDefaultId`), and `PolicyRenewalLayout`
   is also the registered layout for telco's `retention_renewal_offer` and fintech's
   `insurance_protection_feature`.

   ONE CUSTOMER, one set of figures. Everything below is consistent with the Customer 360 demo
   customer that `demoFallbackCustomer360.ts` already describes — 4,820 monthly income, 862 of monthly
   obligations, a 7,640 account balance, `income_stability_score` 82, `delinquency_status` Current,
   `readiness_status` Eligible — so a reader moving between this card and Financial Health finds the
   same person on both.

   `CreditCardNoHoldingLayout` gets NOTHING here, deliberately. It is registered only on the
   `not_held` branch (`registry.ts`: `byHolding({ not_held: CreditCardNoHoldingLayout })`), the
   branch resolves from `credit_card_holding`, and the demo answers that `Active` — a word in neither
   `HELD_ANSWERS` nor `NOT_HELD_ANSWERS`, so `holdingStatusFromAnswer` returns `unknown` and
   `byHolding` falls through to `branches.held`, which is undefined. The section then draws its own
   default body and this layout never mounts. Its two empty blocks are also lists of THIRD-PARTY card
   products (an issuer, a card name, a benefit sentence); the invented entries that once stood there
   were removed, and naming real issuers' products in a recommendation nothing computed is not
   something this file will do.

   Amounts are NUMBERS, not pre-formatted strings, so the layouts format them through the `currency`
   they already receive. The one pre-formatted currency literal elsewhere in this family reached the
   screen without passing through `useLocalization().currency`, and this does not repeat it.
   ============================================================================================== */

/** A tile reading: a figure the layout formats, or a word it prints verbatim. */
export interface DemoDrawnReading {
  key: string;
  value?: number;
  valueText?: string;
}

/** A comparison-panel row: an amount the layout formats, or a word. */
export interface DemoComparisonReading {
  label: string;
  amount?: number;
  text?: string;
}

/** A ranked contribution, pre-formatted the way `IDriverListItem` wants its `value`. */
export interface DemoDriverReading {
  rank: number;
  label: string;
  subLabel?: string;
  value: string;
}

export interface DemoPolicyRenewalLayout {
  policyTiles: DemoDrawnReading[];
  /** The premium-payment donut, as counts. Colour is the layout's — it is a drawing decision. */
  premiumHistory: DemoLabelledCount[];
  recommendation: DemoComparisonReading[];
  positiveDrivers: DemoDriverReading[];
  blockers: DemoDriverReading[];
}

export interface DemoMortgageLayout {
  eligibility: DemoDrawnReading;
  downPayment: DemoDrawnReading;
  capacityTiles: DemoDrawnReading[];
  currentHolding: DemoComparisonReading[];
  recommendation: DemoComparisonReading[];
  homeBuying: DemoComparisonReading[];
  /** The down-payment evidence donut, as amounts summing to the capacity tile above. */
  downPaymentEvidence: DemoLabelledCount[];
  capacityRows: DemoComparisonReading[];
  positiveDrivers: DemoDriverReading[];
  blockers: DemoDriverReading[];
}

/* The customer's own figures, restated once so every block below divides out of the same numbers
   rather than each carrying its own copy. They are `demoFallbackCustomer360.ts`'s. */
const INCOME_MONTHLY = 4820;
const MONTHLY_OBLIGATIONS = 862;
const ACCOUNT_BALANCE = 7640;

/* ---------- Policy Renewal (insurance default; also telco retention and fintech protection) ---- */

/* The policy is 495,000 of cover at 0.46% a year, which is the 2,277 premium. 34.0% of the
   recommended 750,000 is uncovered — the `coverage_gap_signal` tile on Renewal & Coverage
   Opportunities reads exactly 34.0 — and 255,000 is that gap in money. The renewal date is 178 days
   from 21 Sep 2026, which is the days-to-renewal tile beside it. */
export const DEMO_POLICY_RENEWAL_LAYOUT: DemoPolicyRenewalLayout = {
  policyTiles: [
    { key: 'policy-status', valueText: 'Active' },
    { key: 'policy-amount', value: 495000 },
    { key: 'policy-renewal-date', valueText: '18 Mar 2027' },
    { key: 'policy-days-to-renewal', value: 178 },
    { key: 'policy-premium', value: 2277 },
    { key: 'policy-eligibility', valueText: 'Eligible' },
    /* A COUNT, not the `1 settled · 0 open` sentence the frame drew. That exact string is one of the
       fabrications `__tests__/layouts.test.tsx` blacklists by name (`FABRICATED_FIGURES`), and the
       demo layer must not put a removed drawing back on the screen even where it is free to invent a
       reading — the tile's label is `Claims`, so a number answers it. Two claims, both settled,
       which is the experience the `claim_experience_score` 74.2 tile reports. */
    { key: 'policy-claims', value: 2 }
  ],
  /* 125 monthly instalments, 23 of them late or missed — 18.4%, which IS the `premium_payment_risk`
     tile on Policy Health & Claims. The donut and that tile are one measurement drawn twice. */
  premiumHistory: [
    { label: 'On time', value: 102 },
    { label: 'Late', value: 17 },
    { label: 'Missed', value: 6 }
  ],
  /* Every amount at the same 0.46% rate as the policy in force, so the recommendation is the current
     policy scaled rather than a second, unrelated price list. */
  recommendation: [
    { label: 'Current amount', amount: 495000 },
    { label: 'Indicative recommended amount', amount: 750000 },
    { label: 'Increased amount', amount: 255000 },
    { label: 'Updated premium amount', amount: 3450 },
    { label: 'New policy amount', amount: 1000000 },
    { label: 'New policy name', text: 'Secure Shield Plus' },
    { label: 'New policy premium', amount: 4600 }
  ],
  positiveDrivers: [
    { rank: 1, label: 'Policy active through term', subLabel: 'Holding signal', value: '+24' },
    { rank: 2, label: 'Auto-debit on the premium', subLabel: 'Payment signal', value: '+18' },
    { rank: 3, label: 'Single settled claim', subLabel: 'Claim signal', value: '+12' }
  ],
  blockers: [
    { rank: 1, label: 'Cover below recommended', subLabel: 'Coverage signal', value: '-15' },
    { rank: 2, label: '23 instalments late or missed', subLabel: 'Payment signal', value: '-9' },
    { rank: 3, label: 'No rider on the policy', subLabel: 'Coverage signal', value: '-6' }
  ]
};

export const NO_POLICY_RENEWAL_LAYOUT: DemoPolicyRenewalLayout = {
  policyTiles: [],
  premiumHistory: [],
  recommendation: [],
  positiveDrivers: [],
  blockers: []
};

/* ---------- Mortgage / Home Loan (banking, reached from the product dropdown) ------------------ */

/* Sized to the customer rather than to the frame: 862 of existing obligations against 4,820 income
   is a 17.9% DTI, and the 38.5% `affordability_ratio` tile caps total obligations at 1,856 — so the
   headroom is 994 a month, which over 20 years at around 7% supports about 128,000. That is why
   `mortgage_home_loan_readiness` reads 41.6 while the deposit products read in the seventies: the
   constraint is real and the recommendation states it rather than talking past it. */
export const DEMO_MORTGAGE_LAYOUT: DemoMortgageLayout = {
  eligibility: { key: 'mortgage-eligibility', valueText: 'Eligible' },
  downPayment: { key: 'mortgage-down-payment-capacity', value: 18400 },
  capacityTiles: [
    { key: 'mortgage-monthly-income', value: INCOME_MONTHLY },
    { key: 'mortgage-avg-savings-balance', value: ACCOUNT_BALANCE },
    { key: 'mortgage-existing-obligations', value: MONTHLY_OBLIGATIONS },
    // 862 / 4,820 = 17.9%.
    { key: 'mortgage-dti-proxy', value: 17.9 },
    // The `delinquency_status` this customer already carries on the readiness card.
    { key: 'mortgage-credit-history', valueText: 'Current' }
  ],
  currentHolding: [
    { label: 'Primary Banking Relationship', text: 'Primary · 6y 2m' },
    { label: 'Mortgage/Home Loan', text: 'None' },
    { label: 'Observed down-payment capacity', amount: 18400 },
    { label: 'Credit History', text: 'Current' }
  ],
  recommendation: [
    { label: 'Recommended amount', amount: 128000 },
    { label: 'Indicative premium amount', amount: 994 },
    { label: 'Indicative premium capacity', amount: 1856 },
    { label: 'Loan Tenure', text: '20 years' }
  ],
  homeBuying: [
    { label: 'Relationship Tenure', text: '6y 2m' },
    { label: 'Home-interest Window', text: 'Next 6-12 months' },
    { label: 'Property Signals', text: '4 observed' }
  ],
  // Sums to the 18,400 the capacity tile prints, so the donut divides exactly that figure.
  downPaymentEvidence: [
    { label: 'Savings balance', value: ACCOUNT_BALANCE },
    { label: 'Term deposit', value: 6200 },
    { label: 'Investment holdings', value: 3100 },
    { label: 'Other liquid funds', value: 1460 }
  ],
  /* One arithmetic, stated four ways: the 38.5% `affordability_ratio` tile caps obligations at 1,856
     a month, 862 of that is already committed, 994 is what is left, and committing all of it takes
     the ratio back to the 38.5% it started from. */
  capacityRows: [
    { label: 'Estimated affordable monthly payment', amount: 1856 },
    { label: 'Current monthly obligations', amount: MONTHLY_OBLIGATIONS },
    { label: 'Net monthly surplus', amount: 994 },
    { label: 'Indicative DTI after handoff', text: '38.5%' }
  ],
  positiveDrivers: [
    { rank: 1, label: 'Stable income, 82 stability', subLabel: 'Income signal', value: '+21' },
    { rank: 2, label: 'Six-year banking relationship', subLabel: 'Tenure signal', value: '+14' },
    { rank: 3, label: 'No delinquency on record', subLabel: 'Credit signal', value: '+11' }
  ],
  blockers: [
    { rank: 1, label: 'Down-payment capacity 18,400', subLabel: 'Capacity signal', value: '-23' },
    { rank: 2, label: '862 of monthly obligations', subLabel: 'Obligation signal', value: '-17' },
    { rank: 3, label: 'No property under offer', subLabel: 'Intent signal', value: '-8' }
  ]
};

export const NO_MORTGAGE_LAYOUT: DemoMortgageLayout = {
  eligibility: { key: 'mortgage-eligibility' },
  downPayment: { key: 'mortgage-down-payment-capacity' },
  capacityTiles: [],
  currentHolding: [],
  recommendation: [],
  homeBuying: [],
  downPaymentEvidence: [],
  capacityRows: [],
  positiveDrivers: [],
  blockers: []
};

/* ---------- Credit Card / Limit Upgrade, the CARD-HOLDING body ---------------------------------

   NOT `CreditCardNoHoldingLayout`, which still gets nothing here for the reason recorded at the top
   of this file. This is the body `customer360/sections/ProductReadinessSection.tsx` draws ITSELF
   when the registry resolves no layout — the `Credit Card / Limit Upgrade` state for a customer who
   HOLDS a card. It is the last place on Customer 360 that still drew twelve empty tiles and a
   `Not Configured` block, and the registry resolution above it is untouched.

   The same customer as everywhere else in this file: 4,820 of monthly income, 862 of monthly
   obligations, the 38.5% `affordability_ratio` cap, `credit_card_holding` Active,
   `delinquency_status` Current, `income_stability_score` 82 and a 6y 2m banking relationship.

   NO PAN, NO ISSUER, NO THIRD-PARTY PRODUCT. The card number keeps twelve digits hidden, so there
   is no number here to check against anything. `Other Eligible Card Options` names products from
   this tenant's OWN readiness catalogue (`registry/productReadiness.ts`) and nobody else's, and the
   `issuer` column the table type carries is not drawn and not invented. */

/** A card-holding field: `IProfileDetailField` takes its value pre-formatted, as a string. */
export interface DemoProfileField {
  label: string;
  value: string;
}

/** A row of `Other Eligible Card Options`. No `issuer` — see the note above. */
export interface DemoEligibleCard {
  name: string;
  segment: string;
  benefits: string;
  bestValue: string;
  reasons: string[];
}

export interface DemoCreditCardHoldingLayout {
  /** Merged onto the frame's seven labels by label, so the frame stays the authority on wording. */
  cardHolding: DemoProfileField[];
  incomeTiles: DemoDrawnReading[];
  /** `Recommended Limit Upgrade`: two amounts the tile formats, and a ratio it prints verbatim. */
  limitUpgrade: { proposedLimit?: number; increase?: number; increasePercent?: string };
  /** The `Credit Limit` evidence panel. `undefined` leaves today's `Not Configured` block. */
  creditLimit?: { label: string; rows: DemoComparisonReading[] };
  eligibleCards: DemoEligibleCard[];
}

/* At the 5%-of-limit minimum-payment convention the existing 12,000 costs 600 a month, so 262 of the
   862 obligations is everything else; the proposed 18,000 costs 900, and 900 + 262 = 1,162 is 24.1%
   of the 4,820 income — inside the 38.5% cap, which is why the upgrade is recommended at all.
   18,000 - 12,000 = 6,000, and 6,000 / 12,000 = +50%. Used 3,360 of 12,000 is the 28% utilisation. */
const EXISTING_LIMIT = 12000;
const PROPOSED_LIMIT = 18000;
const USED_LIMIT = 3360;

export const DEMO_CREDIT_CARD_HOLDING_LAYOUT: DemoCreditCardHoldingLayout = {
  cardHolding: [
    { label: 'Card', value: 'Credit Card' },
    { label: 'Card Number', value: '•••• •••• •••• 7318' },
    // The `credit_card_holding` answer this customer already carries on the readiness card.
    { label: 'Card Status', value: 'Active' },
    // 12 Mar 2022 to 21 Sep 2026, and inside the 6y 2m banking relationship above it.
    { label: 'Card Age', value: '4y 6m' },
    { label: 'Joined', value: '12 Mar 2022' },
    { label: 'Product Gap', value: 'Limit below assessed capacity' },
    { label: 'Additional Card', value: 'None' }
  ],
  incomeTiles: [
    { key: 'income-monthly', value: INCOME_MONTHLY },
    // 82 `income_stability_score`, which is the High band (`data/confidence.ts`: High >= 80).
    { key: 'income-stability', valueText: 'High' }
  ],
  limitUpgrade: {
    proposedLimit: PROPOSED_LIMIT,
    increase: PROPOSED_LIMIT - EXISTING_LIMIT,
    increasePercent: '+50%'
  },
  creditLimit: {
    label: 'Credit Limit',
    rows: [
      { label: 'Total Limit', amount: EXISTING_LIMIT },
      { label: 'Used Limit', amount: USED_LIMIT },
      { label: 'Available', amount: EXISTING_LIMIT - USED_LIMIT },
      { label: 'Utilisation', text: '28% · Low' }
    ]
  },
  eligibleCards: [
    {
      name: 'Overdraft / Credit Line',
      segment: 'Banking · Revolving',
      benefits: 'Standby line drawn only when the account dips, repaid from the next salary credit',
      bestValue: 'Interest charged on the drawn amount only',
      reasons: ['Readiness 63.2, highest of the alternatives', 'No delinquency on record']
    },
    {
      name: 'Wallet-linked Card',
      segment: 'Wallet · Everyday spend',
      benefits: 'Spends from the wallet balance, so it carries no monthly repayment of its own',
      bestValue: 'Leaves the 38.5% affordability cap untouched',
      reasons: ['Wallet top-up readiness 74.1', 'Adds nothing to the 862 of obligations']
    },
    {
      name: 'BNPL / Credit',
      segment: 'Wallet · Instalments',
      benefits: 'Splits an eligible purchase into three fixed instalments',
      bestValue: 'Instalment sized to the 994 of monthly headroom',
      reasons: ['Clean repayment record', 'Instalment fits inside the remaining headroom']
    }
  ]
};

export const NO_CREDIT_CARD_HOLDING_LAYOUT: DemoCreditCardHoldingLayout = {
  cardHolding: [],
  incomeTiles: [],
  limitUpgrade: {},
  eligibleCards: []
};
